import { IdentityLogo } from '@seek/certsy-ui';
import { CertsyDialog } from '@seek/profile-base-ui';
import { useTranslations } from '@vocab/react';
import { Box, List, Text } from 'braid-design-system';
import { useCallback, useEffect } from 'react';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import type { AnalyticsVerificationsSeekPassDialogCallToAction } from 'src/modules/analytics/types';
import { useAppConfig } from 'src/modules/appConfig';

import type { VerificationsSeekPassDialogAnalyticsOrigin } from '../analyticsUtils';

import translations from './.vocab';

interface Props {
  url: string;
  showDialog: boolean;
  closeDialogFn: () => void;
  origin: VerificationsSeekPassDialogAnalyticsOrigin;
  onSeekPassDialogClick: (arg: {
    callToAction: AnalyticsVerificationsSeekPassDialogCallToAction;
  }) => void;
}

export const VerifyDigitalIdDialog = ({
  url,
  showDialog,
  closeDialogFn,
  origin,
  onSeekPassDialogClick,
}: Props) => {
  const { t } = useTranslations(translations);
  const { brand } = useAppConfig();

  const { seekPassDialogDisplayed } = useAnalytics();

  const onDialogDisplay = useCallback(() => {
    seekPassDialogDisplayed({ origin, certsyType: 'identity' });
  }, [origin, seekPassDialogDisplayed]);

  const onClose = () => {
    closeDialogFn();
  };

  const onCertsyClick = () => {
    onSeekPassDialogClick({
      callToAction: 'verify on certsy',
    });
  };

  const onMaybeLaterClick = () => {
    onSeekPassDialogClick({ callToAction: 'maybe later' });
  };

  useEffect(() => {
    if (showDialog) {
      onDialogDisplay();
    }
  }, [onDialogDisplay, showDialog]);

  const certsyDialogText = (
    <Box paddingX="small">
      <List>
        <Text>
          {t(
            'Strengthen your BRAND Profile with one-time identity verification.',
            { brand },
          )}
        </Text>
        <Text>
          {t(
            'Verifying your identity on SEEK Pass shows potential employers that your identity has been verified...',
          )}
        </Text>
        <Text>{t('Trust in SEEK Pass, a 100% SEEK-owned platform.')}</Text>
        <Text>
          {t('Showcase the SEEK Pass supertick on your Shared Profile.')}
        </Text>
      </List>
    </Box>
  );

  return (
    <CertsyDialog
      title={t('Verify your identity')}
      bodyText={certsyDialogText}
      certsyButtonText={t('Verify on SEEK Pass')}
      seekPassLogo={<IdentityLogo />}
      certsyUrl={url}
      width="small"
      open={showDialog}
      onCertsyClick={onCertsyClick}
      onMaybeLaterClick={onMaybeLaterClick}
      onClose={onClose}
      data={{ automation: 'identity-verify-dialog' }}
    />
  );
};
